import { useState, useEffect } from "react";
import axios from "axios";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { DASHBOARD_TOP_ACHIEVERS } from "../../../routes/routes";
import { styleExcelHeaderRow } from "../../../utils/excelStyles";
import tysIcon from "../../../assets/images/icons/tys.png";
import oneToOneIcon from "../../../assets/images/icons/one_to_one.png";
import referralsIcon from "../../../assets/images/icons/referrals.png";
import visitorsIcon from "../../../assets/images/icons/visitors.png";
import chiefGuestsIcon from "../../../assets/images/icons/chief_guests.png";
import powerDatesIcon from "../../../assets/images/icons/power_dates.png";
import inductionsIcon from "../../../assets/images/icons/inductions.png";

export const ACHIEVER_ICONS = {
  tyfcbs: tysIcon,
  fbms: oneToOneIcon,
  rfls: referralsIcon,
  visitors: visitorsIcon,
  chief_guests: chiefGuestsIcon,
  power_dates: powerDatesIcon,
  inductions: inductionsIcon,
};

export const CATEGORY_ACCENT = {
  tyfcbs: "#0c3c7c",
  fbms: "#f59e0b",
  rfls: "#16a34a",
  visitors: "#2563eb",
  chief_guests: "#7c3aed",
  power_dates: "#db2777",
  inductions: "#0d9488",
};

export const PERIOD_OPTIONS = [
  { value: "this_week", label: "This week" },
  { value: "last_week", label: "Last week" },
  { value: "overall", label: "Overall" },
];

export const getProfileImage = (user, cat) =>
  user?.[cat.imgKey] ||
  user?.thumb_profile_image ||
  user?.thumb_user_profile_image ||
  user?.thumb_from_profile_image ||
  user?.profile_image ||
  user?.user_profile_image ||
  user?.from_profile_image ||
  "";

export const getRawValue = (user, cat) => {
  if (cat.id === "tyfcbs") {
    return Number(user?.tyfcb_amount_without_format || 0);
  }
  return Number(user?.[cat.valKey] || 0);
};

export const getDisplayName = (user, cat) =>
  user?.[cat.nameKey] || user?.[cat.tooltipNameKey] || "—";

/** Always return length-3 array (null = empty slot) */
export const padTop3 = (list) => {
  const arr = Array.isArray(list) ? [...list.slice(0, 3)] : [];
  while (arr.length < 3) arr.push(null);
  return arr;
};

export const canViewTopAchievers = (hasPermission) =>
  typeof hasPermission === "function" &&
  (hasPermission("dashboard-tyns-data-view") ||
    hasPermission("dashboard-121s-data-view") ||
    hasPermission("dashboard-rfls-data-view") ||
    hasPermission("dashboard-visitors-data-view"));

export const getSectionTitle = (selectedWeek) => {
  if (selectedWeek === "last_week") return "Top Achievers of Last Week";
  if (selectedWeek === "overall") return "Top Achievers Overall";
  return "Top Achievers of this Week";
};

export const getPeriodSubtitle = (selectedWeek, currentData) => {
  if (selectedWeek === "overall") return "Overall";
  if (currentData?.week_start_date && currentData?.week_end_date) {
    return `${currentData.week_start_date} - ${currentData.week_end_date}`;
  }
  return "Overall";
};

/** Prefer nested achievers payload; fall back if lists are at root */
export const getAchieversBucket = (currentData) => {
  if (!currentData || typeof currentData !== "object") return {};
  if (currentData.thisWeekTop3AchieversList) {
    return currentData.thisWeekTop3AchieversList;
  }
  if (
    currentData.tyfcbsList ||
    currentData.onetooneList ||
    currentData.referralsList
  ) {
    return currentData;
  }
  return {};
};

const asList = (value) => (Array.isArray(value) ? value : []);

export const buildCategories = (currentData) => {
  const bucket = getAchieversBucket(currentData);

  return [
    {
      id: "tyfcbs",
      permission: "dashboard-tyns-data-view",
      title: "Biz Given",
      list: asList(bucket.tyfcbsList),
      totalCount: Number(bucket.tyfcbsTotalCount || 0),
      overallTitle: "Overall",
      overallValue: bucket.thisWeekOverallTyfcb,
      valKey: "tyfcb_amount",
      formatVal: (v) => `${v}`,
      metricLabel: "",
      imgKey: "thumb_user_profile_image",
      nameKey: "user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "fbms",
      permission: "dashboard-121s-data-view",
      title: "R2R",
      list: asList(bucket.onetooneList),
      totalCount: Number(bucket.onetooneTotalCount || 0),
      overallTitle: "Overall",
      overallValue: bucket.thisWeek121s,
      valKey: "one2one_count",
      formatVal: (v) => `${v}`,
      metricLabel: "meetings",
      imgKey: "thumb_from_profile_image",
      nameKey: "from_user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "rfls",
      permission: "dashboard-rfls-data-view",
      title: "RFLs",
      list: asList(bucket.referralsList),
      totalCount: Number(bucket.referralsTotalCount || 0),
      overallTitle: "Overall",
      overallValue: bucket.thisWeekRfls,
      valKey: "referrals_count",
      formatVal: (v) => `${v}`,
      metricLabel: "referrals",
      imgKey: "thumb_from_profile_image",
      nameKey: "from_user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "visitors",
      permission: "dashboard-visitors-data-view",
      title: "General Visitors",
      list: asList(bucket.generalVisitorsList),
      totalCount: Number(bucket.generalVisitorsTotalCount || 0),
      overallTitle: "Overall",
      overallValue: bucket.thisWeekVisitors,
      valKey: "visitors_count",
      formatVal: (v) => `${v}`,
      metricLabel: "visitors",
      imgKey: "thumb_user_profile_image",
      nameKey: "user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "chief_guests",
      permission: "dashboard-visitors-data-view",
      title: "Chief Guests",
      list: asList(bucket.cheifGuestVisitorsList),
      totalCount: Number(bucket.cheifGuestVisitorsTotalCount || 0),
      overallTitle: "Overall",
      overallValue: bucket.thisWeekChiefGuests,
      valKey: "visitors_count",
      formatVal: (v) => `${v}`,
      metricLabel: "guests hosted",
      imgKey: "thumb_user_profile_image",
      nameKey: "user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "inductions",
      permission: "dashboard-visitors-data-view",
      title: "Inductions",
      list: asList(bucket.inductionsVisitorsList),
      totalCount: Number(bucket.inductionsVisitorsTotalCount || 0),
      overallTitle: "Overall",
      overallValue: bucket.thisWeekInductions,
      valKey: "visitors_count",
      formatVal: (v) => `${v}`,
      metricLabel: "Inductions",
      imgKey: "thumb_user_profile_image",
      nameKey: "user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "power_dates",
      permission: "dashboard-visitors-data-view",
      title: "Power Dates",
      list: asList(bucket.powerDateVisitorsList),
      totalCount: Number(bucket.powerDateVisitorsTotalCount || 0),
      overallTitle: "Overall",
      overallValue: bucket.thisWeekPowerDates,
      valKey: "visitors_count",
      formatVal: (v) => `${v}`,
      metricLabel: "power dates",
      imgKey: "thumb_user_profile_image",
      nameKey: "user_name",
      tooltipNameKey: "from_user_name",
    },
  ];
};

const cleanFilterParams = (activeFilter = {}) => {
  const params = {};
  Object.entries(activeFilter || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    params[key] = value;
  });
  return params;
};

export function useTopAchievers({ data, ovalLoading, filter }) {
  const [excelLoading, setExcelLoading] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState("this_week");
  const [customAchieversData, setCustomAchieversData] = useState(null);
  const [internalLoading, setInternalLoading] = useState(true);
  const [weekOptions, setWeekOptions] = useState(null);

  const fetchTopAchievers = async (
    weekType = "this_week",
    activeFilter = filter,
  ) => {
    try {
      setInternalLoading(true);
      const params = {
        ...cleanFilterParams(activeFilter),
        week_type: weekType,
      };
      const response = await axios.get(DASHBOARD_TOP_ACHIEVERS, { params });
      if (response.status === 200 && response.data.success) {
        setCustomAchieversData(response.data.data);
        if (response.data.data?.weekOptions) {
          setWeekOptions(response.data.data.weekOptions);
        }
      }
    } catch (error) {
      console.error("Error fetching top achievers:", error);
    } finally {
      setInternalLoading(false);
    }
  };

  // Always load from dedicated API so all design variants get list data
  useEffect(() => {
    fetchTopAchievers(selectedWeek, filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selectedWeek, data]);

  const handlePeriodChange = (weekType) => {
    if (weekType === selectedWeek) return;
    setSelectedWeek(weekType);
  };

  const dashboardData = data && !Array.isArray(data) ? data : null;
  const currentData = customAchieversData || dashboardData || {};
  const currentLoading =
    internalLoading ||
    (ovalLoading && !customAchieversData && !dashboardData);

  const downloadExcel = async (activeCategories) => {
    try {
      setExcelLoading(true);
      const workbook = new Excel.Workbook();
      const sheetColumns = [
        { header: "S.No", key: "sno", width: 10 },
        { header: "Name", key: "name", width: 30 },
        { header: "Company", key: "company", width: 30 },
        { header: "Value", key: "value", width: 15 },
        { header: "Country", key: "country", width: 20 },
        { header: "State", key: "state", width: 20 },
        { header: "Region", key: "region", width: 20 },
        { header: "Chapter", key: "chapter", width: 20 },
      ];
      const thinBorder = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      activeCategories.forEach((cat) => {
        const worksheet = workbook.addWorksheet(cat.title.substring(0, 31));
        worksheet.columns = sheetColumns;
        styleExcelHeaderRow(worksheet.getRow(1), worksheet.columns.length);

        (cat.list || []).forEach((user, i) => {
          worksheet.addRow({
            sno: i + 1,
            name: user[cat.tooltipNameKey] || user[cat.nameKey] || "",
            company: user.user_company_name || "",
            value: user[cat.valKey] || 0,
            country: user.country_name || "",
            state: user.state_name || "",
            region: user.region_name || "",
            chapter: user.chapter_name || "",
          });
        });

        worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          if (rowNumber > 1) {
            row.eachCell((cell) => {
              cell.border = thinBorder;
            });
          }
        });
      });

      const periodLabel =
        selectedWeek === "overall"
          ? "Overall"
          : `${currentData?.week_start_date}_to_${currentData?.week_end_date}`;
      const buf = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buf]), `Top_Achievers_${periodLabel}.xlsx`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error.message);
    } finally {
      setExcelLoading(false);
    }
  };

  return {
    excelLoading,
    selectedWeek,
    weekOptions,
    currentData,
    currentLoading,
    handlePeriodChange,
    downloadExcel,
  };
}
